import { Check, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { TEXT_CAPTION } from '../recipes'
import type { VoiceQualityView } from './voiceprint-types'

export interface VoiceQualityProps {
  /** Null where the read does not judge the sound: nothing is drawn. */
  quality: VoiceQualityView | null
  className?: string
}

/**
 * The sound of what a voiceprint was made from, in words: "Sound quality: good", or "Sound
 * quality: low · short stretch of speech, noise". It is about the recording — its length, the
 * room, the microphone — and never about how the person speaks; a low reading means a comparison
 * with this voiceprint deserves less trust. An icon and words, the warning ink only when it is
 * low.
 */
export function VoiceQuality({ quality, className }: VoiceQualityProps) {
  const { t } = useTranslation()
  if (!quality) return null
  const low = quality.level === 'low'
  const Icon = low ? TriangleAlert : Check
  const reasons = quality.flags.map(flag => t(`humint.voice.mark.quality.reasons.${flag}`))
  return (
    <p
      data-quality={quality.level}
      className={cn(
        TEXT_CAPTION,
        'flex items-start gap-1.5',
        low ? 'text-warning-foreground' : 'text-muted-foreground',
        className
      )}
    >
      <Icon aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      <span>
        {t(`humint.voice.mark.quality.${quality.level}`)}
        {low && reasons.length > 0 && ` · ${reasons.join(', ')}`}
      </span>
    </p>
  )
}
